import React, { ReactElement, useCallback, useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useForm } from 'react-hook-form'
import { yupResolver } from '@hookform/resolvers/yup'
import * as yup from 'yup'
import TextInput from '../../common/form/TextInput'
import FormRow from '../../common/form/FormRow'
import FormFieldBox from '../../common/form/FormFieldBox'
import SpinnableSubmitButton from '../../common/form/SpinnableSubmitButton'
import { useFormSubmission } from '../../common/servercall/useFormSubmission'
import ErrorAlert from '../../common/components/ErrorAlert'
import { UseFormSetValue } from 'react-hook-form/dist/types/form'
import { VpRole } from '../../app/types'
import { api } from '../../common/api'
import { Accordion, AccordionDetails, AccordionSummary, Checkbox, FormControlLabel, FormGroup, Typography } from '@mui/material'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'

type FormData = {
    name: string
    description: string
    privileges: string[]
}

const schema = yup.object().shape({
    name: yup.string().trim().max(50, 'too-long').required('required-m'),
    description: yup.string().trim().max(200, 'too-long').nullable(),
    privileges: yup.array().of(yup.string()).default([]),
})

type Props = { entry: VpRole | null; onSubmit: (formData: FormData) => void }

export type ReturnProps = {
    handleSubmit: () => void
    form: () => ReactElement
    submitButton: () => ReactElement
    reset: () => void
    setValue: UseFormSetValue<FormData>
}

type PrivilegeGroup = { section: string; privileges: string[] }

export const groupPrivileges = (all: string[], t: (key: string, fallback?: string) => string): PrivilegeGroup[] => {
    const enterprise: string[] = [], site: string[] = [], trunk: string[] = []
    all.forEach(p => {
        if (p.startsWith('ENTERPRISE_')) enterprise.push(p)
        else if (p.startsWith('TRUNK_')) trunk.push(p)
        else if (p.startsWith('SITE_')) site.push(p)
    })
    const byLabel = (a: string, b: string) => formatPrivilegeName(a, t).localeCompare(formatPrivilegeName(b, t))
    enterprise.sort(byLabel)
    site.sort(byLabel)
    trunk.sort(byLabel)
    return [{ section: 'enterprise', privileges: enterprise }, { section: 'site', privileges: site }, { section: 'trunk', privileges: trunk }].filter(g => g.privileges.length > 0)
}

export const formatPrivilegeName = (privilege: string, t: (key: string, fallback?: string) => string): string => {
    const key = `admin-management.privilege.${privilege}`
    const val = t(key, '')
    if (val && val !== key) return val
    return privilege.replace(/^ENTERPRISE_/, '').replace(/^TRUNK_/, '').replace(/^SITE_/, '').replace(/_/g, ' ').toLowerCase().replace(/^\w/, c => c.toUpperCase())
}

type PrivilegeSelectorProps = {
    privilegeGroups: PrivilegeGroup[]
    initialPrivileges: string[]
    onPrivilegesChange: (privileges: string[]) => void
}

const PrivilegeSelector = React.memo(({ privilegeGroups, initialPrivileges, onPrivilegesChange }: PrivilegeSelectorProps) => {
    const { t } = useTranslation()
    const [selected, setSelected] = useState<string[]>(initialPrivileges)

    useEffect(() => {
        setSelected(initialPrivileges)
    }, [initialPrivileges])

    const toggle = useCallback((privilege: string) => {
        setSelected(prev => {
            const updated = prev.includes(privilege)
                ? prev.filter(p => p !== privilege)
                : [...prev, privilege]
            onPrivilegesChange(updated)
            return updated
        })
    }, [onPrivilegesChange])

    return (
        <>
            <Typography variant='subtitle2' sx={{ mt: 2, mb: 1 }}>{t('admin-management.role-privileges')}</Typography>
            {privilegeGroups.map(group => (
                <Accordion key={group.section} defaultExpanded variant='outlined' sx={{ mb: 1 }}>
                    <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                        <Typography variant='subtitle2'>{t(`admin-management.privilege-section.${group.section}`)}</Typography>
                    </AccordionSummary>
                    <AccordionDetails>
                        <FormGroup>
                            {group.privileges.map(priv => (
                                <FormControlLabel key={priv}
                                                  control={<Checkbox size='small' checked={selected.includes(priv)} onChange={() => toggle(priv)} />}
                                                  label={formatPrivilegeName(priv, t)} />
                            ))}
                        </FormGroup>
                    </AccordionDetails>
                </Accordion>
            ))}
        </>
    )
})

const useVpRoleForm = ({ entry, onSubmit }: Props): ReturnProps => {
    const { t } = useTranslation()
    const [availablePrivileges, setAvailablePrivileges] = useState<string[]>([])

    useEffect(() => {
        api.get('/api/enterprise/vpPrivileges').then(({ data }) => setAvailablePrivileges(data)).catch(() => {
        })
    }, [])

    const privilegeGroups = useMemo(() => groupPrivileges(availablePrivileges, t), [availablePrivileges, t])

    const { control, handleSubmit, setError, reset, setValue } = useForm<FormData>({
        resolver: yupResolver(schema),
        defaultValues: { name: '', description: '', privileges: [] },
    })

    const initialPrivileges = useMemo(() => entry?.privileges || [], [entry])

    useEffect(() => {
        if (entry) {
            reset({ name: entry.name, description: entry.description, privileges: entry.privileges || [] })
        } else {
            reset({ name: '', description: '', privileges: [] })
        }
    }, [entry, reset])

    const onPrivilegesChange = useCallback((updated: string[]) => {
        setValue('privileges', updated)
    }, [setValue])

    const { submit, isSubmitting, serverError, onError } = useFormSubmission<FormData>(onSubmit, setError)

    return {
        setValue, reset,
        handleSubmit: handleSubmit(submit, onError),
        form: () => entry ? (
            <>
                <ErrorAlert errorKey={typeof serverError === 'string' ? serverError : undefined} showAlert={!!serverError} />
                <FormRow>
                    <FormFieldBox fullWidth={true}>
                        <TextInput autofocus={true} label={t('admin-management.role-name')} name={'name'} control={control} required={true} />
                    </FormFieldBox>
                    <FormFieldBox fullWidth={true}>
                        <TextInput autofocus={false} label={t('admin-management.role-description')} name={'description'} control={control} />
                    </FormFieldBox>
                </FormRow>
                <PrivilegeSelector privilegeGroups={privilegeGroups} initialPrivileges={initialPrivileges} onPrivilegesChange={onPrivilegesChange} />
            </>
        ) : <div />,
        submitButton: () => entry ? <SpinnableSubmitButton label={t(entry.id ? 'general.form-update' : 'general.form-create')} showSpinner={isSubmitting} /> : <></>,
    }
}

export default useVpRoleForm
